import { JsonFilterOperation } from '@repo/alictus-common/types/runtime/filter_operation';
import { DateFilter, FilterType } from '@repo/alictus-common/enums/date_filter';
import { differenceInDays, differenceInHours, fromUnixTime, format, add, sub, set } from 'date-fns';
import jsonata from 'jsonata';

// Log buffer interface for collecting debug logs
interface LogEntry {
	keyName: string;
	formattedValue: string;
}

export async function calculateJsonAtaQueryResult(
	jsonAtaFilterOperation: JsonFilterOperation,
	playFabData: any,
	currentTimeEpoch: number | null = null,
	gmtOffset: number | null = null,
	stickySegmentUid?: string,
): Promise<number | boolean> {
	// Create a log buffer to collect all print values
	const logBuffer: LogEntry[] = [];

	try {
		const hasOperatorType = jsonAtaFilterOperation.QueryValues.some(
			(v) => typeof v.Type === 'string' && v.Type.toLowerCase() === 'operator',
		);

		// If there is an operator type, find QueryValue.QueryName in the Query and replace this with QueryValue.Value
		if (hasOperatorType) {
			for (const variable of jsonAtaFilterOperation.QueryValues) {
				if (typeof variable.Type === 'string' && variable.Type.toLowerCase() === 'operator') {
					const queryName = variable.QueryName;
					const value = variable.Value;
					if (typeof value !== 'string') {
						console.log(`[FilterJsonAta] Value for QueryName '${queryName}' is not a string or number: ${JSON.stringify(value)}`);
						throw new Error(`[FilterJsonAta] Value for QueryName '${queryName}' is not a string or number: ${JSON.stringify(value)}`);
					}
					if (jsonAtaFilterOperation.Query.includes(queryName)) {
						jsonAtaFilterOperation.Query = jsonAtaFilterOperation.Query.replaceAll(queryName, value); // Use global replace to handle multiple occurrences
					} else {
						console.log(`[FilterJsonAta] QueryName '${queryName}' not found in Query.`);
						throw new Error(`[FilterJsonAta] QueryName '${queryName}' not found in Query.`);
					}
				}
			}
		}

		const expr = jsonata(jsonAtaFilterOperation.Query);
		const hasDateType = jsonAtaFilterOperation.QueryValues.some(
			(v) => typeof v.Type === 'string' && (v.Type.toLowerCase().includes('dayssince') || v.Type.toLowerCase().includes('hourssince')),
		);

		// Register printValue function with access to logBuffer
		expr.registerFunction('printValue', (keyName: string, value: any) => collectPrintValue(keyName, value, logBuffer), '<sx:x>');

		if (hasDateType) {
			expr.registerFunction(
				'CalculateDaysSince',
				(playfabEpochValue: number, clientEpoch: number, clientGMTOffset: number) =>
					calculateDateSince(playfabEpochValue, DateFilter.DaysSince, clientEpoch, clientGMTOffset),
				'<nnn:n>',
			);

			expr.registerFunction(
				'CalculateHoursSince',
				(playfabEpochValue: number, clientEpoch: number, clientGMTOffset: number) =>
					calculateDateSince(playfabEpochValue, DateFilter.HoursSince, clientEpoch, clientGMTOffset),
				'<nnn:n>',
			);

			if (currentTimeEpoch !== null) {
				expr.assign('clientEpoch', currentTimeEpoch);
			} else {
				console.log(`[FilterJsonAta] No currentTimeEpoch provided`);
				throw new Error(`[FilterJsonAta] No currentTimeEpoch provided!`);
			}

			if (gmtOffset !== null) {
				expr.assign('clientGMTOffset', gmtOffset);
			} else {
				console.log(`[FilterJsonAta] No gmtOffset provided`);
				throw new Error(`[FilterJsonAta] No gmtOffset provided!`);
			}
		}

		for (const variable of jsonAtaFilterOperation.QueryValues) {
			expr.assign(variable.QueryName, variable.Value);
		}

		if (jsonAtaFilterOperation.Filter === FilterType.Sticky) {
			expr.assign('segmentUid', stickySegmentUid);
		}

		const result = await expr.evaluate(playFabData);
		//console.log(`[FilterJsonAta] JSONata evaluation result: ${JSON.stringify(result)}`);

		if (jsonAtaFilterOperation.Filter === FilterType.Sticky) {
			return result; // 0: Passthrough, 1: True, 2: False
		} else {
			return !!result; // Convert to boolean for truthy/falsy evaluation
		}
	} catch (error) {
		// Error case - now print all buffered logs for debugging
		if (logBuffer.length > 0) {
			console.log(`[FilterJsonAta] Debug logs collected during evaluation:`);
			logBuffer.forEach((entry) => {
				console.log(`[FilterJsonAta] Printing value for key '${entry.keyName}':\n${entry.formattedValue}`);
			});
		}

		console.log(`[FilterJsonAta] JSONata evaluation error: ${JSON.stringify(error)}`);
		throw new Error(`[FilterJsonAta] JSONata evaluation error: ${(error as any).message}`);
	}
}

function collectPrintValue(keyName: string, value: any, logBuffer: LogEntry[]): any {
	let formattedValue: string;

	try {
		if (value === null) {
			formattedValue = 'null';
		} else if (value === undefined) {
			formattedValue = 'undefined';
		} else if (typeof value === 'string') {
			formattedValue = `"${value}"`;
		} else if (typeof value === 'number' || typeof value === 'boolean') {
			formattedValue = String(value);
		} else if (Array.isArray(value)) {
			if (value.length === 0) {
				formattedValue = '[]';
			} else if (value.length <= 10) {
				// Show first 10 items in detail
				formattedValue = `[\n${value
					.map((item, index) => `  [${index}]: ${typeof item === 'object' ? JSON.stringify(item, null, 2).replace(/\n/g, '\n    ') : item}`)
					.join(',\n')}\n]`;
			} else {
				// Show first 5 items and summary for large arrays
				const firstFive = value
					.slice(0, 5)
					.map((item, index) => `  [${index}]: ${typeof item === 'object' ? JSON.stringify(item, null, 2).replace(/\n/g, '\n    ') : item}`)
					.join(',\n');
				formattedValue = `[\n${firstFive},\n  ... and ${value.length - 5} more items\n] (Total: ${value.length} items)`;
			}
		} else if (typeof value === 'object') {
			// Handle objects
			const keys = Object.keys(value);
			if (keys.length === 0) {
				formattedValue = '{}';
			} else if (keys.length <= 5) {
				formattedValue = JSON.stringify(value, null, 2);
			} else {
				// Show first 5 keys for large objects
				const firstFiveKeys = keys.slice(0, 5);
				const partialObj = firstFiveKeys.reduce((acc, key) => {
					acc[key] = value[key];
					return acc;
				}, {} as any);
				formattedValue = `${JSON.stringify(partialObj, null, 2).slice(0, -1)}  ... and ${keys.length - 5} more keys\n} (Total: ${keys.length} keys)`;
			}
		} else {
			// Fallback for other types
			formattedValue = JSON.stringify(value);
		}
	} catch (error) {
		// If JSON.stringify fails, use toString
		formattedValue = `[Error formatting value: ${error}] - Raw: ${String(value)}`;
	}

	// Collect the log entry instead of immediately printing
	logBuffer.push({
		keyName,
		formattedValue,
	});
}

function calculateDateSince(playfabEpochValue: number, dateFilter: DateFilter, clientEpoch: number, clientGMTOffset: number): number {
	const dateSince = applyDateFilter(playfabEpochValue, dateFilter, clientEpoch, clientGMTOffset, 'playfabEpochValue');
	if (dateSince === null) {
		return -99999999; // or handle error as needed
	}
	return dateSince;
}

function applyDateFilter(
	playFabValue: any,
	dateFilter: DateFilter,
	currentTimeEpoch: number,
	gmtOffset: number,
	keyToFetch: string, // Added for logging context
): number | null {
	if (typeof playFabValue !== 'number' || playFabValue <= 0) {
		if (typeof playFabValue !== 'number') {
			console.error(
				`[FilterJsonAta] Date Filter Error: playFabValue for key '${keyToFetch}' is not a number, it is: ${typeof playFabValue} ('${playFabValue}'). Cannot apply date filter.`,
			);
		}

		return null; // Indicate failure
	}

	const playfabEpochTimestamp: number = playFabValue;
	const currentDate = fromUnixTime(currentTimeEpoch); // Assumed to be in correct timezone already

	// Convert PlayFab UTC epoch timestamp to Date object
	const clientUTC = fromUnixTime(playfabEpochTimestamp);

	// Apply GMT offset to get the client's local date/time
	// Using add works for both positive and negative offsets
	const clientLocalDate = add(clientUTC, { hours: gmtOffset });

	switch (dateFilter) {
		case DateFilter.DaysSince: {
			// Calculate difference in calendar days (midnight to midnight)
			// Reverting to comparing local midnightDate to client's midnight representation
			const clientMidnightDate = set(clientLocalDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
			const localMidnightDate = set(currentDate, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }); // Use server's UTC midnight
			return differenceInDays(localMidnightDate, clientMidnightDate);
		}

		case DateFilter.HoursSince: {
			const diffInHours = differenceInHours(currentDate, clientLocalDate);
			return diffInHours;
		}

		default:
			console.error(`[FilterJsonAta] Unsupported DateFilter: ${dateFilter}`);
			return null; // Indicate failure for unsupported filter
	}
}
